/**
 * Location List Feature Initializer
 *
 * This file is the entry point for the Location List feature.
 * It imports and initializes all the necessary modules for this feature to work,
 * such as state management, UI interactions, map handling, and navigation.
 */
import { state, els, initEls, initState } from "./state.js";
import { initMap, createMapContainer, initMapWithBoundary } from "./map.js";
import { loadMapData } from "./markers.js";
import { initNavigation } from "./navigation.js";
import { initTippyManager } from "../../services/tippy-manager.js";
import { fetchAndDisplayMarkers } from "./markers.js";
import { setupEventListeners } from "./ui.js";

/**
 * Wait for map to be ready using Promise-based approach
 * @returns {Promise} Resolves when map is ready
 */
function waitForMapReady() {
  return new Promise((resolve) => {
    // Check if map is already ready
    if (
      window.locationListMap &&
      window.locationListMap.getBounds &&
      window.locationListMap.getCenter &&
      window.locationListMap.getZoom
    ) {
      try {
        const center = window.locationListMap.getCenter();
        const zoom = window.locationListMap.getZoom();
        if (center && zoom !== undefined) {
          console.log("Map is already ready, resolving immediately");
          resolve();
          return;
        }
      } catch (error) {
        console.warn("Map appears ready but has errors:", error);
      }
    }

    // Set up event listener for mapReady event
    const handleMapReady = () => {
      console.log("Map ready event received");
      document.removeEventListener("mapReady", handleMapReady);
      resolve();
    };

    document.addEventListener("mapReady", handleMapReady);

    // Fallback timeout
    setTimeout(() => {
      console.warn("Map ready timeout, resolving anyway");
      document.removeEventListener("mapReady", handleMapReady);
      resolve();
    }, 5000);
  });
}

/**
 * Initialize location list feature with lazy map initialization
 */
export async function initLocationList() {
  console.log("=== INITIALIZING LOCATION LIST FEATURE ==="); // Debug log

  // Step 0: Initialize elements and state
  initEls();
  initState();

  // Check if map element exists
  if (!els.mapElement) {
    console.error("Map element not found, skipping initialization");
    return;
  }

  // Step 1: Create map container with loading state
  if (!createMapContainer()) {
    console.error("Failed to create map container");
    return;
  }

  // Step 2: Get regions data for boundary calculation
  const regions = els.mapElement?.dataset.regions;
  console.log("Regions data for boundary calculation:", regions);

  let boundaryData = [];

  // Step 3: Fetch boundary data if available
  if (regions) {
    try {
      const fetchBoundaryData = async (osm_id) => {
        try {
          const response = await fetch(`/wp-content/themes/iphub/osm/${osm_id}.txt`);
          const geojsonData = await response.json();

          // Extract coordinates for center calculation
          if (geojsonData.features && geojsonData.features.length > 0) {
            const coordinates = geojsonData.features[0].geometry.coordinates;
            if (coordinates && coordinates.length > 0) {
              return coordinates[0]; // Return coordinates from first polygon
            }
          }
          return [];
        } catch (error) {
          console.error("Error fetching boundary data:", error);
          return [];
        }
      };

      if (typeof regions === "string") {
        if (regions.includes(",")) {
          const regionIds = regions.split(",");
          const boundaryPromises = regionIds.map((osm_id) => fetchBoundaryData(osm_id));
          const results = await Promise.all(boundaryPromises);
          boundaryData = results.flat();
        } else {
          boundaryData = await fetchBoundaryData(regions);
        }
      } else if (typeof regions === "number") {
        boundaryData = await fetchBoundaryData(regions);
      }

      console.log("Boundary data collected:", boundaryData.length, "coordinates");
    } catch (error) {
      console.error("Error collecting boundary data:", error);
    }
  }

  // Step 4: Initialize map with calculated center
  if (boundaryData.length > 0) {
    console.log("Initializing map with boundary-based center");
    initMapWithBoundary(boundaryData, 10);
  } else {
    console.log("No boundary data available, using fallback initialization");
    initMap();
  }

  // Step 5: Wait for map to be ready
  await waitForMapReady();

  // Step 6: Load map data
  await loadMapData();

  // Step 7: Initialize additional features
  initNavigation();
  initTippyManager();

  // Step 8: Setup event listeners
  setupEventListeners();

  console.log("=== LOCATION LIST FEATURE INITIALIZED ==="); // Debug log
}
